import type React from 'react'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'

import {
  BatchSearch,
  CircularProgress,
  type HeadItemType,
  Icon,
  Input,
  Modal,
  Table,
  Toggle,
} from 'components'
import type { CustomBoolean } from 'constants/enums'
import { Controller, useForm } from 'react-hook-form'
import { getClientHierarchy } from 'services/client/hierarchy'

import * as S from './styles'
export interface ClientsListInterface {
  cd_clie: string
  nm_pcro: string
  nr_cnpj_cpf: string
  key: string
  fl_sele: CustomBoolean
  checked?: boolean
  nr_pedi_clie?: string
  text?: boolean
  index: number
}
interface ModalPostOnProps {
  open: boolean
  orderNumber: string
  setOpen: Dispatch<SetStateAction<boolean>>
  handlePostOn: (clients: ClientsListInterface[]) => void
  handleCancel: () => void
}

const ModalPostOn: React.FC<ModalPostOnProps> = ({
  open,
  orderNumber,
  setOpen,
  handlePostOn,
  handleCancel,
}) => {
  const [filter, setFilter] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)

  const [records, setRecords] = useState<Array<ClientsListInterface>>([])

  const { control, setValue, reset, getValues } = useForm<ClientsListInterface[]>({
    defaultValues: { ...records },
    mode: 'onChange',
    reValidateMode: 'onBlur',
  })

  const {
    control: controlSearch,
    reset: resetSearch,
    watch,
  } = useForm({ defaultValues: { search: '' } })
  const search = watch('search')

  const getClientList = async () => {
    setLoading(true)

    const returnApi = await getClientHierarchy({
      nm_clie: '',
      nr_pedi_rasc: orderNumber,
    })
    const returnApiWithSelected = []

    for (const element of returnApi) {
      returnApiWithSelected.push({
        ...element,
        key: `${element.cd_clie}-${element.nr_cnpj_cpf}`,
      })
    }
    const newRecords: ClientsListInterface[] = returnApiWithSelected.map(
      (record: ClientsListInterface, index: number) => ({
        ...record,
        checked: false,
        index: index,
      }),
    )
    setRecords(newRecords)
    setLoading(false)
    return newRecords
  }

  const filterRecords = () => {
    const filteredRecords: Array<ClientsListInterface> = []
    if (search !== '') {
      search
        .split(',')
        .filter((item: string) => item.trim() !== '')
        .forEach((element: string) => {
          filteredRecords.push(
            ...records.filter((item) => {
              return (
                item.nm_pcro.toLowerCase().includes(element.toLowerCase()) ||
                item.cd_clie.toLowerCase().includes(element.toLowerCase())
              )
            }),
          )
        })
    } else {
      filteredRecords.push(...records)
    }

    return filteredRecords
  }

  useEffect(() => {
    if (records.length) reset({ ...records })
    else if (!filter.length) getClientList()
  }, [records.length])

  useEffect(() => {
    if (open) {
      getClientList()
    }
    return function cleanup() {
      reset()
      setFilter('')
    }
  }, [open])

  const tableLabels: HeadItemType[] = [
    { columnName: 'SAP', label: 'SAP' },
    { columnName: 'RazAOSocial', label: 'Razão Social' },
    { columnName: 'NumeroPedidoCliente', label: 'Nº Pedido Cliente' },
    { columnName: 'Texto', label: 'Texto' },
  ]

  const setMaterialSelection = (checked: boolean, id?: string) => {
    if (id) {
      const newRecords = records.map((record, index) => {
        if (index === Number(id)) {
          return { ...record, checked: checked }
        }
        return record
      })
      setValue(`${Number(id)}.checked`, checked)
      setRecords(newRecords)
    } else {
      const filteredRecords = filterRecords()
      const newRecords = filteredRecords.map((record) => {
        return { ...record, checked: checked }
      })
      records.forEach((val, key) => {
        if (newRecords[key]?.checked) {
          setValue(`${key}.checked`, checked)
        }
      })
      setRecords(newRecords)
    }
  }
  const getRecordsData = () => {
    const filteredRecords = filterRecords()
    const recordsData = filteredRecords?.map((item, key) => {
      return {
        checked: item?.checked,
        content: [
          item.cd_clie,
          item.nm_pcro,
          <div key={`${item.key}-${key}-nr_pedi_clie`} style={{ marginBottom: -40 }}>
            <Controller
              control={control}
              defaultValue=""
              name={`${key}.nr_pedi_clie`}
              render={({ field: { name, value, onChange } }) => (
                <Input
                  InputProps={{
                    inputProps: { maxLength: 23 },
                  }}
                  name={name}
                  onChange={onChange}
                  value={value ?? ''}
                />
              )}
            />
          </div>,
          <Controller
            control={control}
            defaultValue={true}
            key={`${item.key}-${key}-text`}
            name={`${key}.text`}
            render={({ field: { value } }) => (
              <Toggle
                disabled={false}
                processChange={() => {
                  setValue(`${key}.text`, !value)
                }}
                setValue={() => {
                  return
                }}
                value={Boolean(value)}
              />
            )}
          />,
        ],
        id: item.index.toString(),
      }
    })
    return recordsData
  }

  return (
    <Modal
      buttonAction={() => {
        const arrayValues = Object.values(getValues())?.filter((record) => record.checked)
        handlePostOn(arrayValues)
      }}
      buttonDisabled={!Object.values(getValues())?.filter((record) => record.checked).length}
      buttonIcon={<Icon icon="copy" size="15" />}
      buttonText="Copiar para selecionados"
      open={open}
      returnAction={handleCancel}
      returnText="Cancelar"
      setOpen={setOpen}
      showReturn
      title="Listar Pedido"
    >
      <S.ModalContent>
        <S.ModalInfo>
          Selecione para quais clientes deseja copiar o <strong>Pedido #{orderNumber}</strong>
        </S.ModalInfo>
        <Controller
          control={controlSearch}
          name="search"
          render={({ field }) => (
            <BatchSearch
              label="Cliente"
              onClear={() => {
                resetSearch({ search: '' })
              }}
              placeHolder="Buscar por número SAP"
              {...field}
            />
          )}
        />

        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <S.TableLabel>CNPJ&rsquo;s vinculados</S.TableLabel>
            <S.WrapTableStyled>
              <Table
                bodyData={getRecordsData()}
                hasCheckbox
                headData={tableLabels}
                notFoundDescription="Cheque o texto que digitou e tente novamente."
                notFoundTitle="Nenhum cliente encontrado"
                setChecked={setMaterialSelection}
              />
            </S.WrapTableStyled>
          </>
        )}
      </S.ModalContent>
    </Modal>
  )
}

export default ModalPostOn
